import React, { useId, useState } from "react";

import {
  ResilienceReport,
  ScoreTile,
} from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import Dropdown from "../Dropdown";
import { BandLegend, K, Section, useDomainName, useHalfLabel } from "./parts";
import {
  band,
  sentenceBand,
  DOMAINS,
  Domain,
  DOMAIN_THEMES,
  formatChange,
  formatScore,
  nextHalf,
  previousHalf,
} from "./resilience-view";
import styles from "./resilience.module.scss";

type Order = "highest" | "lowest";

// Safari drops a list's role once its markers are styled away.
const LIST = "list";

// Hidden domains stay at the bottom whichever way the list runs.
const sorted = (domains: Record<string, ScoreTile>, order: Order): Domain[] => {
  const shown = DOMAINS.filter((domain) => !domains[domain].suppressed);
  const hidden = DOMAINS.filter((domain) => domains[domain].suppressed);
  const value = (domain: Domain) => domains[domain].value as number;
  shown.sort((a, b) =>
    order === "highest" ? value(b) - value(a) : value(a) - value(b)
  );
  return [...shown, ...hidden];
};

// A greyed-out pair of arrows where a change will show once there is a
// previous half to compare with.
const ChangePlaceholder = ({ className }: { className: string }) => (
  <span className={className} aria-hidden="true">
    {"\u2191\u2193"}
  </span>
);

// A score out of 100 with its bar: a domain's in its band colour, a theme's
// in grey, both in the same column so a score never reads as a change.
const ScoreBar = ({ value, banded }: { value: number; banded: boolean }) => {
  const { t, i18n } = useTranslation(["admin"]);
  return (
    <>
      <p className={styles["row-value"]}>
        <strong className={styles["row-score"]}>
          {formatScore(value, i18n.language)}
        </strong>
        <span className={styles["row-out-of"]}>{t(`${K}.outOf`)}</span>
      </p>
      <div
        className={styles["row-bar"]}
        data-band={banded ? band(value) : undefined}
        aria-hidden="true"
      >
        <span
          className={styles["row-bar-fill"]}
          style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
        />
      </div>
    </>
  );
};

const Delta = ({
  change,
  awaitingChange,
}: {
  change: ScoreTile;
  awaitingChange: boolean;
}) => {
  const { t, i18n } = useTranslation(["admin"]);
  if (change.suppressed) {
    return awaitingChange ? (
      <ChangePlaceholder className={styles["row-delta-placeholder"]} />
    ) : (
      <span />
    );
  }
  const direction =
    change.value > 0 ? "up" : change.value < 0 ? "down" : "level";
  const label = formatChange(change.value, i18n.language);
  return (
    <span className={styles["row-delta"]} data-direction={direction}>
      <span aria-hidden="true">
        {direction === "up" ? "\u2191 " : direction === "down" ? "\u2193 " : ""}
        {label}
      </span>
      <span className={styles["visually-hidden"]}>
        {t(`${K}.domainBreakdown.change`, {
          count: Math.abs(change.value),
          change: label,
        })}
      </span>
    </span>
  );
};

const Themes = ({
  id,
  domain,
  report,
  open,
  awaitingChange,
}: {
  id: string;
  domain: Domain;
  report: ResilienceReport;
  open: boolean;
  awaitingChange: boolean;
}) => {
  const { t } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const themes = report.scores.selected.all_staff.themes;
  const changes = report.scores.change.themes;
  return (
    <ul
      id={id}
      className={styles["themes"]}
      role={LIST}
      hidden={!open}
      aria-label={t(`${K}.domainBreakdown.themesLabel`, {
        domain: domainName(domain),
      })}
    >
      {DOMAIN_THEMES[domain].map((theme) => (
        <li key={theme} className={styles["theme"]}>
          <span className={styles["theme-name"]}>
            {t(`${K}.themes.${theme}`)}
          </span>
          <div className={styles["row-middle"]}>
            {themes[theme].suppressed ? (
              <span className={styles["row-not-enough"]}>
                {t(`${K}.notEnoughData`)}
              </span>
            ) : (
              <ScoreBar value={themes[theme].value} banded={false} />
            )}
          </div>
          {!themes[theme].suppressed && (
            <Delta change={changes[theme]} awaitingChange={awaitingChange} />
          )}
        </li>
      ))}
    </ul>
  );
};

const DomainRow = ({
  domain,
  report,
  awaitingChange,
}: {
  domain: Domain;
  report: ResilienceReport;
  awaitingChange: boolean;
}) => {
  const { t } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const themesId = useId();
  const [open, setOpen] = useState(false);
  const tile = report.scores.selected.all_staff.domains[domain];
  const change = report.scores.change.domains[domain];
  const expandable = DOMAIN_THEMES[domain].length > 0 && !tile.suppressed;
  const sentence = tile.suppressed ? null : sentenceBand(tile.value);

  const name = (
    <>
      {expandable ? (
        <span className={styles["row-chevron"]} aria-hidden="true" />
      ) : (
        <span className={styles["row-chevron-space"]} aria-hidden="true" />
      )}
      {domainName(domain)}
    </>
  );

  return (
    <li className={styles["row"]}>
      <div className={styles["row-main"]}>
        {expandable ? (
          <button
            type="button"
            className={styles["row-head"]}
            aria-expanded={open}
            aria-controls={themesId}
            onClick={() => setOpen((was) => !was)}
          >
            {name}
          </button>
        ) : (
          <div className={styles["row-head"]}>{name}</div>
        )}
        <div className={styles["row-middle"]}>
          {tile.suppressed ? (
            <span className={styles["row-not-enough"]}>
              {t(`${K}.notEnoughData`)}
            </span>
          ) : (
            <>
              <ScoreBar value={tile.value} banded />
              <p className={styles["row-sentence"]}>
                {t(`${K}.domainBreakdown.bandSentence.${sentence}`, {
                  domain: domainName(domain),
                })}
              </p>
            </>
          )}
        </div>
        {!tile.suppressed && (
          <Delta change={change} awaitingChange={awaitingChange} />
        )}
      </div>
      {expandable && (
        <Themes
          id={themesId}
          domain={domain}
          report={report}
          open={open}
          awaitingChange={awaitingChange}
        />
      )}
    </li>
  );
};

const Domains = ({ report }: { report: ResilienceReport }) => {
  const { t } = useTranslation(["admin"]);
  const halfLabel = useHalfLabel();
  const sortId = useId();
  const [order, setOrder] = useState<Order>("highest");
  const domains = report.scores.selected.all_staff.domains;
  const { change } = report.scores;
  const changeShown = [
    ...Object.values(change.domains),
    ...Object.values(change.themes),
  ].some((tile) => !tile.suppressed);
  // The trend is withheld only on the first published half, so no previous
  // half exists to change against. A change hidden later for too few people
  // leaves its place blank instead: hidden, not "not yet".
  const awaitingChange = report.trend.suppressed;

  return (
    <Section
      title={t(`${K}.sections.domains`)}
      info={t(`${K}.domainBreakdown.info`)}
    >
      <div className={styles["domains-bar"]}>
        <p className={styles["card-description"]}>
          {changeShown
            ? t(`${K}.domainBreakdown.descriptionWithChange`, {
                period: halfLabel(previousHalf(report.period_value)),
              })
            : t(`${K}.domainBreakdown.description`)}
        </p>
        <span className={styles["sort"]}>
          <span id={sortId} className={styles["period-label"]}>
            {t(`${K}.domainBreakdown.sort`)}
          </span>
          <Dropdown
            labelId={sortId}
            align="end"
            value={order}
            onChange={(next) => setOrder(next as Order)}
            options={[
              {
                value: "highest",
                label: t(`${K}.domainBreakdown.highestFirst`),
              },
              {
                value: "lowest",
                label: t(`${K}.domainBreakdown.lowestFirst`),
              },
            ]}
          />
        </span>
      </div>
      <ul
        className={styles["rows"]}
        role={LIST}
        aria-label={t(`${K}.domainBreakdown.listLabel`)}
      >
        {sorted(domains, order).map((domain) => (
          <DomainRow
            key={domain}
            domain={domain}
            report={report}
            awaitingChange={awaitingChange}
          />
        ))}
      </ul>
      <BandLegend />
      {awaitingChange && (
        <p className={styles["changes-note"]}>
          <ChangePlaceholder className={styles["changes-note-icon"]} />
          <span>
            {t(`${K}.domainBreakdown.changesFrom`, {
              next: halfLabel(nextHalf(report.period_value)),
            })}
          </span>
        </p>
      )}
    </Section>
  );
};

export default Domains;
